/* eslint-disable @typescript-eslint/promise-function-async */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import {
  StyledBox,
  StyledContent,
  StyledDotWrapper,
  StyledDragIcon,
  StyledIconWrapper,
} from "./styles";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ActionCandidate, TriggerCandidate } from "../../types/candidate";
import {
  DotsMenu,
  Icon,
  MenuOption,
  Paragraph,
  Tooltip,
} from "@smartsuite/react-ui/lib";
import { WarningIcon } from "../AddTriggerButton/styles";
import { useRootStore } from "../../stores/hooks";
import {
  capitalize,
  translateAction,
  translateTrigger,
} from "../../locales/i18n";
import type {
  ActionDescription,
  IntegrationDescription,
  TriggerDescription,
  InputDescription,
  InputConfig,
} from "@smartsuite/automation-engine-api";
import { countInvalidInputsValidations } from "../../stores/automation/utils/inputValidation";
import { getIntegrationLogo } from "../../utils/integration-logo";
import { observer } from "mobx-react-lite";
import { getLocalCssColorVariables } from "../../utils/cssVariablesUtils";
import { mergeDuplicateAction } from "../../stores/automation/mergers/actions";
import {
  ActionLabel,
  INPUT_APPLICATION_ID,
  Integrations,
  isSmartSuiteIntegration,
  TriggerLabel,
} from "../../utils/constants";
import { actionApplicationId } from "../../utils/record-list-identity";
import { useTranslation } from "react-i18next";
import { StyledArrowIconWrapper } from "../../containers/AutomationForm/Selectors/style";
import { TriggerMenu } from "../TriggerMenu";
import { ActionMenu } from "../ActionMenu";
import { findInputAndPick, pickValueConfigString } from "../../utils/inputs";
import { MenuOptionList } from "../ActionMenu/styles";
import { Colors, GrayColors, SolutionColorVariables } from "@smartsuite/types";
import { useFlowCanvasMenuDismiss } from "../FlowCanvas/hooks/use-flow-canvas-menu-dismiss";

interface AddedEventButtonProps {
  trigger?: TriggerCandidate;
  action?: ActionCandidate;
  error: boolean;
  isHighlighted: boolean;
  isActive: boolean;
  onClick: () => void;
  eventType: "action" | "trigger";
  disabled?: boolean;
  onDelete?: (e: React.MouseEvent, hash: string) => void;
  onChange?: (integrationId: string, triggerId: string) => void;
  onDuplicate?: (hash: string, instanceId?: number) => void;
  fieldMissingError: boolean;
  groupType: "actions" | "loop" | "branches";
}

const getIconClass = (actionId: string): string | undefined => {
  switch (actionId) {
    case ActionLabel.findRecords:
      return "find-records";
    case ActionLabel.mergeRecords:
      return "merge-records";
  }
};

export const AddedEventButton = observer<AddedEventButtonProps>(
  ({
    error,
    trigger,
    isActive,
    isHighlighted,
    eventType,
    action,
    disabled,
    fieldMissingError,
    groupType,

    onClick,
    onChange,
    onDelete,
    onDuplicate,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();
    const [openDotMenu, setOpenDotMenu] = useState<boolean>(false);
    const candidate = store.automations.candidate;
    const applications = store.applications;

    const [triggerIntegration, setTriggerIntegration] = useState<
      IntegrationDescription | undefined
    >();
    const [integrationTrigger, setIntegrationTrigger] = useState<
      TriggerDescription | undefined
    >();
    const [actionIntegration, setActionIntegration] = useState<
      IntegrationDescription | undefined
    >();
    const [integrationAction, setIntegrationAction] = useState<
      ActionDescription | undefined
    >();
    const changeTriggerRef = useRef<HTMLDivElement>(null);
    const changeActionRef = useRef<HTMLDivElement>(null);
    const [openTrigger, setOpenTrigger] = useState<boolean>(false);
    const [openAction, setOpenAction] = useState<boolean>(false);

    useFlowCanvasMenuDismiss(
      useCallback(() => {
        setOpenDotMenu(false);
        setOpenAction(false);
        setOpenTrigger(false);
      }, []),
    );

    const showError = (
      error: boolean,
      invalidInputs: number,
      credentialId?: string,
      triggerPermissionsError?: string,
      descriptionError?: boolean,
    ): boolean => {
      return (
        (error && invalidInputs > 0) ||
        (error && !credentialId) ||
        descriptionError === true ||
        triggerPermissionsError !== undefined
      );
    };

    useEffect(() => {
      setTriggerIntegration(
        store.integrations.list.find(
          (i) => i.integrationId === trigger?.triggerReference?.integrationId,
        ),
      );
    }, [store.integrations.list, trigger]);

    useEffect(() => {
      setActionIntegration(
        store.integrations.list.find(
          (i) => i.integrationId === action?.actionReference?.integrationId,
        ),
      );
    }, [store.integrations.list, action]);

    useEffect(() => {
      setIntegrationTrigger(
        triggerIntegration?.triggers.find(
          (t) => t.triggerId === trigger?.triggerReference?.triggerId,
        ),
      );
    }, [trigger?.triggerReference?.triggerId, triggerIntegration?.triggers]);

    useEffect(() => {
      setIntegrationAction(
        actionIntegration?.actions.find(
          (t) => t.actionId === action?.actionReference?.actionId,
        ),
      );
    }, [action?.actionReference?.actionId, actionIntegration?.actions]);

    const countInvalidInputs = (
      inputs: InputConfig[],
      descriptions: InputDescription[],
    ): number => {
      return countInvalidInputsValidations(
        inputs,
        descriptions,
        candidate.self.trigger.triggerReference?.triggerId ===
          TriggerLabel.matchesCondition
          ? candidate.self.trigger.conditions
          : undefined,
      );
    };

    const getToolTipText = (
      numberInvalidInputs: number,
      action?: ActionCandidate,
    ): string => {
      if (action?.triggerAllowedError) {
        return t("automation-form.action-is-not-available-for-trigger-error");
      }
      return t("automation-form.configuration-error", {
        count: numberInvalidInputs,
      });
    };

    if (eventType === "trigger" && trigger !== undefined) {
      const LogoIcon = getIntegrationLogo(
        trigger.triggerReference!.integrationId,
      );
      let applicationName = "";

      const selectedApp = findInputAndPick(
        trigger.inputs,
        INPUT_APPLICATION_ID,
        pickValueConfigString,
      );

      if (selectedApp) {
        applicationName =
          applications.list.find((app) => app.id === selectedApp)?.label ?? "";
      }

      const isSmartSuite = isSmartSuiteIntegration(
        trigger.triggerReference!.integrationId,
      );

      const isTriggerActionable =
        disabled !== true ||
        (disabled &&
          (trigger.integrationDisabled === true ||
            trigger.triggerDisabled === true));

      const isTriggerDisabled =
        trigger.integrationDisabled === true ||
        trigger.triggerDisabled === true;

      let triggerErrorMessage = getToolTipText(
        countInvalidInputs(trigger.inputs, trigger.description.inputs),
      );
      if (trigger.triggerDisabled === true) {
        triggerErrorMessage = t("caption.Trigger is disabled");
      } else if (trigger.integrationDisabled === true) {
        triggerErrorMessage = t("caption.Integration is disabled");
      }

      return (
        <div>
          <StyledBox
            onClick={!isTriggerDisabled ? onClick : undefined}
            isActive={isActive}
            isHighlighted={isHighlighted}
            showError={
              showError(
                error,
                countInvalidInputs(trigger.inputs, trigger.description.inputs),
                trigger.credentialId,
                store.automations.candidate.triggerPermissionsError,
                trigger.integrationDisabled === true ||
                  trigger.triggerDisabled === true,
              ) || fieldMissingError
            }
            ref={changeTriggerRef}
            disabled={isTriggerDisabled}
          >
            <StyledIconWrapper>
              <LogoIcon />
            </StyledIconWrapper>
            <StyledContent>
              <Paragraph
                className="trigger-title-text"
                size="xxs"
                color="secondary"
              >
                {capitalize(t("general.trigger.one"))}
              </Paragraph>
              <button
                type="button"
                onClick={() =>
                  isTriggerActionable && setOpenTrigger(!openTrigger)
                }
                className="description-wrapper description-wrapper_trigger"
              >
                <Paragraph
                  className="trigger-description"
                  weight="semibold"
                  size="m"
                  wrapMode="ellipsis"
                  transition
                >
                  {`${translateTrigger(integrationTrigger ?? {})}`}
                </Paragraph>
                {isTriggerActionable && (
                  <StyledArrowIconWrapper className="arrow-icon-wrapper">
                    <Icon
                      size="S"
                      name={openTrigger ? "arrow-up-small" : "arrow-down-small"}
                      className={"arrow-icon"}
                      style={{
                        width: "13px",
                        height: "13px",
                        fill: openTrigger
                          ? SolutionColorVariables.sc1
                          : "currentcolor",
                      }}
                    />
                  </StyledArrowIconWrapper>
                )}
              </button>
              {isSmartSuite && (
                <Paragraph
                  className="title-application"
                  size="xxs"
                  color="secondary"
                >
                  {`${
                    trigger.triggerReference?.integrationId !==
                    Integrations.webhooks
                      ? "In "
                      : ""
                  }${capitalize(applicationName)}`}
                </Paragraph>
              )}
            </StyledContent>
            <WarningIcon>
              {(showError(
                error,
                countInvalidInputs(trigger.inputs, trigger.description.inputs),
                trigger.credentialId,
                store.automations.candidate.triggerPermissionsError,
                trigger.integrationDisabled === true ||
                  trigger.triggerDisabled === true,
              ) ||
                fieldMissingError) && (
                <Tooltip
                  placements={["top"]}
                  text={triggerErrorMessage}
                  maxWidth={350}
                  target={
                    <Icon
                      data-testid="warning-icon"
                      size={"M"}
                      name={"important-lined"}
                      color={Colors.bRed}
                      width={26}
                      height={26}
                    />
                  }
                />
              )}
            </WarningIcon>
          </StyledBox>
          <TriggerMenu
            menuButtonRef={changeTriggerRef as MutableRefObject<HTMLElement>}
            isMenuOpen={openTrigger}
            setMenuOpen={setOpenTrigger}
            onTriggerChange={(integrationId, triggerId) => {
              if (triggerId === trigger?.triggerReference?.triggerId) {
                setOpenTrigger(false);
                return;
              }
              onChange?.(integrationId, triggerId);
            }}
          />
        </div>
      );
    }
    if (
      eventType === "action" &&
      action !== undefined &&
      onDelete !== undefined
    ) {
      const LogoIcon = getIntegrationLogo(
        action.actionReference!.integrationId,
        action.actionReference!.actionId,
      );

      let applicationName = "";

      const selectedApp = actionApplicationId(action);

      if (selectedApp) {
        applicationName =
          applications.list.find((app) => app.id === selectedApp)?.label ?? "";
      }

      const isSmartSuite = isSmartSuiteIntegration(
        action.actionReference!.integrationId,
      );

      const isActionActionalbe =
        disabled !== true ||
        (disabled &&
          (action.integrationDisabled === true ||
            action.actionDisabled === true));
      let actionErrorMessage =
        action === undefined
          ? undefined
          : getToolTipText(
              countInvalidInputs(action.inputs, action.description.inputs),
              action,
            );
      if (action?.accessError === true) {
        actionErrorMessage = t(
          "Edits of the automation are restricted. Your permissions do not allow you to have access to this action",
        );
      } else if (action?.actionDisabled === true) {
        actionErrorMessage = t("caption.Action is disabled");
      } else if (action?.integrationDisabled === true) {
        actionErrorMessage = t("caption.Integration is disabled");
      }
      return (
        <div>
          <StyledBox
            onClick={
              disabled
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    onClick();
                  }
            }
            isActive={isActive}
            isHighlighted={isHighlighted}
            showError={
              showError(
                error,
                countInvalidInputs(action.inputs, action.description.inputs),
                action.credentialId,
                undefined,
              ) || fieldMissingError
            }
            ref={changeActionRef}
            disabled={disabled === true}
          >
            <StyledDragIcon>
              <Icon color={GrayColors["G-4"]} name="drag" size="M" />
            </StyledDragIcon>
            <StyledIconWrapper
              className={getIconClass(action.actionReference!.actionId)}
            >
              <LogoIcon />
            </StyledIconWrapper>
            <StyledContent>
              <Paragraph
                className="action-title-text"
                size="xxs"
                color="secondary"
              >
                {capitalize(t("general.action.one"))}
              </Paragraph>
              <button
                type="button"
                disabled={disabled === true}
                onClick={() => isActionActionalbe && setOpenAction(!openAction)}
                className={"description-wrapper"}
              >
                <Paragraph
                  className={`action-description ${
                    disabled === true ? "disabled" : ""
                  }`}
                  weight="semibold"
                  size="m"
                  wrapMode="ellipsis"
                  transition
                >
                  {`${translateAction(integrationAction ?? {}, action)}`}
                </Paragraph>
                {!action.accessError && (
                  <StyledArrowIconWrapper className="arrow-icon-wrapper">
                    <Icon
                      size="S"
                      name={openAction ? "arrow-up-small" : "arrow-down-small"}
                      style={{
                        width: "13px",
                        height: "13px",
                        fill: openAction
                          ? SolutionColorVariables.sc1
                          : "currentcolor",
                      }}
                    />
                  </StyledArrowIconWrapper>
                )}
              </button>
              {isSmartSuite && applicationName.length > 0 && (
                <Paragraph
                  className="title-application"
                  size="xxs"
                  color="secondary"
                >
                  {`In ${capitalize(applicationName)}`}
                </Paragraph>
              )}
            </StyledContent>

            {/* Dot menu */}
            {!action.accessError && (
              <StyledDotWrapper open={isActive || openDotMenu}>
                <DotsMenu
                  data-testid="dots-menu"
                  opened={openDotMenu}
                  onToggle={setOpenDotMenu}
                  size="s"
                  minWidth={200}
                  at="bottom"
                  my="top+12"
                  smallListPaddings={true}
                  backgroundColors={{
                    hover: SolutionColorVariables.sc2,
                    active: SolutionColorVariables.sc2,
                  }}
                  colors={{
                    default: Colors.dGrey,
                    hover: SolutionColorVariables.sc1,
                    active: SolutionColorVariables.sc1,
                  }}
                  portalStyles={getLocalCssColorVariables(store.theme)}
                  disabled={disabled === true && !isActionActionalbe}
                >
                  <MenuOptionList>
                    {!disabled && (
                      <MenuOption
                        disabled={
                          candidate.totalStepCount >=
                          store.featureFlags.limits.maxActionsPerAutomation
                        }
                        key="duplicate-action"
                        onClick={(e) => {
                          e.stopPropagation();

                          if (onDuplicate) {
                            onDuplicate(
                              action.hash,
                              action?.actionReference?.instanceId,
                            );
                          }

                          if (!onDuplicate) {
                            candidate.setActions(
                              [mergeDuplicateAction(action.hash)],
                              groupType,
                            );
                          }

                          setOpenDotMenu(false);
                        }}
                        iconName={"layers"}
                        iconColor={SolutionColorVariables.sc1}
                        iconSize={"S"}
                        iconSmallMargin
                      >
                        {t("3dot-menu.duplicate-action")}
                      </MenuOption>
                    )}
                    <MenuOption
                      key="delete-action"
                      onClick={(e) => {
                        onDelete(e, action.hash);
                        setOpenDotMenu(false);
                      }}
                      iconName={"trash-1"}
                      iconColor={Colors.bRed}
                      iconSize={"S"}
                      iconSmallMargin
                    >
                      {t("general.delete-action")}
                    </MenuOption>
                  </MenuOptionList>
                </DotsMenu>
              </StyledDotWrapper>
            )}

            {/* Error Icon */}
            <WarningIcon>
              {(showError(
                error,
                countInvalidInputs(action.inputs, action.description.inputs),
                action.credentialId,
                undefined,
                action.accessError ||
                  action.deletedError ||
                  action.triggerAllowedError ||
                  action.integrationDisabled === true ||
                  action.actionDisabled === true,
              ) ||
                fieldMissingError) && (
                <Tooltip
                  placements={["top"]}
                  text={actionErrorMessage}
                  maxWidth={350}
                  target={
                    <Icon
                      size={"M"}
                      name={"important-lined"}
                      color={Colors.bRed}
                      width={26}
                      height={26}
                    />
                  }
                />
              )}
            </WarningIcon>
          </StyledBox>
          <ActionMenu
            showAdvancedLogic={groupType === "actions"}
            // ActionMenu no longer gates Conditional Logic on hasBranches
            // internally (Nested Advanced Logic, Task 3) -- preserve this
            // consumer's pre-existing behavior (CL hidden once a branches
            // block exists anywhere) explicitly.
            advancedLogicItems={{
              branches: !candidate.branches.hasBranches,
            }}
            filterActions={groupType === "actions" ? [] : ["merge-records"]}
            menuButtonRef={changeActionRef as MutableRefObject<HTMLElement>}
            isMenuOpen={openAction}
            setMenuOpen={setOpenAction}
            onActionChange={(integrationId, actionId) => {
              if (actionId === action?.actionReference?.actionId) {
                setOpenAction(false);
                return;
              }
              onChange?.(integrationId, actionId);
            }}
          />
        </div>
      );
    }
    return null;
  },
);
